/**
 * State prize-claim guides.
 *
 * Every fact here is sourced from the state lottery's own published pages
 * (or an official state government source, where noted). Do not add a state
 * unless each figure below can be cited to an official source — the `sources`
 * array is rendered on the page so readers can verify.
 */

export interface ClaimTier {
  amount: string;
  where: string;
}

export interface ClaimGuideFaq {
  question: string;
  answer: string;
}

export interface ClaimGuideSource {
  label: string;
  url: string;
}

export interface ClaimGuide {
  stateSlug: string;
  stateName: string;
  lotteryName: string;
  officialUrl: string;
  intro: string;
  tiers: ClaimTier[];
  deadline: string[];
  documents: string[];
  taxes: string[];
  anonymity: string[];
  mailAddress: string;
  claimCenterCities: string[];
  claimCenterHours: string;
  payoutChoice: string[];
  faqs: ClaimGuideFaq[];
  sources: ClaimGuideSource[];
  lastReviewed: string; // ISO date the facts were checked against official sources
}

const texas: ClaimGuide = {
  stateSlug: "texas",
  stateName: "Texas",
  lotteryName: "Texas Lottery",
  officialUrl: "https://www.texaslottery.com",
  intro:
    "Where you can claim a Texas Lottery prize depends entirely on how much you won. Small prizes are paid over the counter at any retailer, mid-range prizes go through one of 16 regional claim centers, and every jackpot is processed at the Austin headquarters by appointment. The details below come straight from the Texas Lottery's published claim rules.",
  tiers: [
    {
      amount: "$599 or less",
      where:
        "Any Texas Lottery retailer, a claim center, Austin HQ, or by mail. Retailers are authorised to pay these prizes but are not required to; they may pay by check or money order at no charge.",
    },
    {
      amount: "$600 – $999,999",
      where: "A Texas Lottery claim center, Austin HQ, or by mail with a completed claim form.",
    },
    {
      amount: "Over $1,000,000 up to $2,500,000",
      where: "Any Texas Lottery claim center, or Austin HQ.",
    },
    {
      amount: "Over $2,500,000 up to $5,000,000",
      where: "Only the Dallas, Fort Worth, Houston or San Antonio claim centers, or Austin HQ.",
    },
    {
      amount: "Over $5,000,000, all Lotto Texas / Powerball / Mega Millions jackpots, and any annuity prize",
      where: "Texas Lottery headquarters in Austin only, by appointment.",
    },
  ],
  deadline: [
    "Draw game prizes are forfeited after the 180th day following the draw date.",
    "Scratch ticket prizes are forfeited after the 180th day following the official end-of-game date for that ticket.",
    "The deadline can be extended for eligible military personnel — call the Texas Lottery on 800-375-6886.",
  ],
  documents: [
    "The winning ticket, signed on the back.",
    "A completed Texas Lottery claim form. You can print one, or request one from the Texas Lottery by phone, email or through the Request a Claim Form page.",
    "A Social Security Number or Tax ID is required on prizes of $25 or more for U.S. citizens and resident aliens.",
    "Prizes from $1 up to $5,000,000 may be claimed by mail; larger prizes must be claimed in person.",
  ],
  taxes: [
    "Federal withholding is 24% of gross proceeds (winnings minus the wager) when gross proceeds exceed $5,000 for U.S. citizens and residents.",
    "The IRS requires reporting when winnings are $600 or more and at least 300 times the wager, or whenever the prize is subject to withholding.",
    "Nonresident aliens have 30% withheld on prizes of $600 or more.",
    "Texas levies no state personal income tax, so no state income tax is withheld from a Texas Lottery prize. Withholding is not the same as your final tax bill — a large prize can push you into a higher federal bracket.",
  ],
  anonymity: [
    "Texas law lets winners of $1,000,000 or more choose, on the day they claim, to keep their personally identifiable information out of public disclosure.",
    "The choice must be made at the time of claim — it cannot be applied afterwards. Prizes under $1,000,000 are not covered by this provision.",
  ],
  mailAddress: "Texas Lottery, ATTN: Austin Claim Center, PO Box 16600, Austin, TX 78761-6600",
  claimCenterCities: [
    "Abilene",
    "Amarillo",
    "Austin",
    "Beaumont",
    "Corpus Christi",
    "Dallas",
    "El Paso",
    "Fort Worth",
    "Houston",
    "Laredo",
    "Lubbock",
    "McAllen",
    "Odessa",
    "San Antonio",
    "Tyler",
    "Victoria",
  ],
  claimCenterHours: "Monday to Friday, 8:00 a.m. – 5:00 p.m. local time.",
  payoutChoice: [
    "This is the part Texas does differently from most states: the cash value option versus annual payments must be chosen when the ticket is purchased, and it cannot be changed later.",
    "There is no post-win election window. If you want the lump sum, say so at the counter before you pay.",
  ],
  faqs: [
    {
      question: "Where can I cash a small Texas Lottery ticket?",
      answer:
        "Any Texas Lottery retailer can pay prizes of $599 or less, though they are not obliged to. You can also claim by mail or at a claim center.",
    },
    {
      question: "How long do I have to claim a Texas Lottery prize?",
      answer:
        "180 days from the draw date for draw games, or 180 days from the official end-of-game date for scratch tickets. Eligible military personnel can request an extension.",
    },
    {
      question: "Can I stay anonymous if I win the lottery in Texas?",
      answer:
        "Yes, for prizes of $1,000,000 or more. You must make that election at the time you claim the prize; it is not available for smaller prizes.",
    },
    {
      question: "How much tax is withheld from a Texas Lottery win?",
      answer:
        "24% federal withholding applies once gross proceeds exceed $5,000 (30% for nonresident aliens on prizes of $600 or more). Texas has no state income tax, so nothing is withheld at state level.",
    },
    {
      question: "Can I switch from annuity to a lump sum after winning in Texas?",
      answer:
        "No. Texas requires the cash value option or annual payments to be selected when the ticket is bought, and the choice is final.",
    },
    {
      question: "Can I claim a Texas Lottery prize by mail?",
      answer:
        "Yes, for prizes from $1 up to $5,000,000. Send the signed ticket and a completed claim form to the Austin Claim Center PO Box; use trackable mail for larger amounts.",
    },
  ],
  sources: [
    {
      label: "Texas Lottery — Claim Your Prize",
      url: "https://www.texaslottery.com/export/sites/lottery/Winners/Claim_Your_Prize/index.html",
    },
    {
      label: "Texas Lottery — Claim Center Locations",
      url: "https://www.texaslottery.com/export/sites/lottery/About_Us/Agency_Directory/Claim_Center_Locations.html",
    },
    { label: "Texas Lottery — FAQ", url: "https://www.texaslottery.com/export/sites/lottery/FAQ/index.html" },
    {
      label: "Texas Lottery — Request a Claim Form",
      url: "https://www.texaslottery.com/opencms/Winners/Claim_Your_Prize/Request_Claim_Form.jsp",
    },
    {
      label: "Texas Open Data Portal — Winners list (Gov't Code §466.411 anonymity)",
      url: "https://data.texas.gov/See-Category-Tile/Winners-List-of-Texas-Lottery-Prizes/54pj-3dxy/about_data",
    },
  ],
  lastReviewed: "2026-08-26",
};

export const CLAIM_GUIDES: ClaimGuide[] = [texas];

export function getClaimGuide(stateSlug: string): ClaimGuide | undefined {
  return CLAIM_GUIDES.find((g) => g.stateSlug === stateSlug);
}

export function hasClaimGuide(stateSlug: string): boolean {
  return CLAIM_GUIDES.some((g) => g.stateSlug === stateSlug);
}
